import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { generateDataTestId } from 'src/utils';

import { Room as RoomType } from '../../system/rooms';

interface AddResourceButtonProps {
  label: string;
  value: number;
  onIncrement: () => void;
  disabled?: boolean;
  dataProps?: RoomType | string | undefined;
  highlighted?: boolean;
}

/**
 * Field with increment button
 */
const AddResourceButton: React.FC<AddResourceButtonProps> = ({
  label,
  value,
  onIncrement,
  disabled = false,
  dataProps,
  highlighted = false,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        mr: 3,
        ...(highlighted && {
          backgroundColor: 'warning.light',
          borderRadius: '16px',
          border: '1.5px solid',
          borderColor: 'warning.main',
          px: 1.5,
          py: 0.25,
        }),
      }}
    >
      {highlighted && (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: 'warning.main',
            mr: 0.75,
            flexShrink: 0,
          }}
        />
      )}
      <IconButton
        data-testid={generateDataTestId(
          `${dataProps}`,
          undefined,
          `${label}-incrementButton`,
        )}
        size="small"
        onClick={onIncrement}
        disabled={disabled}
        sx={{
          p: 0,
          color: disabled
            ? 'grey.400'
            : highlighted
              ? 'warning.dark'
              : 'info.dark',
          border: '1.5px solid',
          borderColor: disabled
            ? 'grey.400'
            : highlighted
              ? 'warning.dark'
              : 'info.dark',
          '&:hover': {
            backgroundColor: disabled
              ? 'transparent'
              : highlighted
                ? 'warning.dark'
                : 'info.dark',
            color: disabled
              ? 'grey.400'
              : highlighted
                ? 'warning.contrastText'
                : 'info.contrastText',
          },
        }}
      >
        <AddIcon fontSize="small" sx={{ fontSize: 12, fontWeight: 'bold' }} />
      </IconButton>

      <Typography
        component="span"
        variant="body2"
        sx={{
          fontWeight: 'bold',
          ml: 1,
          mr: 0.5,
        }}
      >
        {value}
      </Typography>

      <Typography
        component="span"
        variant="body2"
        sx={{
          fontWeight: 'bold',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

export default AddResourceButton;
